import type { UploadedCvtDiagnostic } from '@packages/contexts/qmr-cvt-diagnostic';
import type { Qmr } from '@packages/models/api';

import { LoadingCircle, Typography } from '@packages/ui/shared';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { DATE_FORMAT } from '@web/config/constants';
import { formatDateWithTimeAgo } from '@web/utils/date-format';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { DiagnosticLink } from './diagnostic-link';
import { DiagnosticResultBadge } from './diagnostic-result-badge';
import { DiagnosticSummaryCard } from './diagnostic-summary-card';

const useStyles = createUseStyles({
    headingRow: {
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
});

type DiagnosticSummaryProps = {
    /** Whether the report's CVT diagnostic can be changed. The cards show a button only while it can. */
    canEditDiagnostic: boolean;

    /** Whether the uploaded diagnostic is still processing. */
    isUploadedDiagnosticProcessing: boolean;

    /** Callback for the "Change Test" button. */
    onChangeDiagnosticPress: () => void;

    /** Callback for the "Search Test" button. */
    onSearchDiagnosticPress: () => void;

    /** The `diagnosticId` of the CVT diagnostic attached, if one exists. */
    cvtDiagnosticsJobId?: string;

    /** The report's record of the attached diagnostic, with its result and the date it completed. */
    sbrResponse?: Qmr['sbrResponse'];

    /** The VIN of the vehicle associated with the NQR. */
    vin?: string;

    /** The diagnostic uploaded from the report, whose id shows under the attached one while it processes. */
    uploadedDiagnostic?: UploadedCvtDiagnostic;
};

/**
 * A summary of where the report's CVT diagnostic stands.
 *
 * Renders the attached diagnostic, noting an uploaded one that's still processing, a processing upload, or that none is
 * attached, with a button only while the diagnostic can be changed.
 */
export function DiagnosticSummary({
    canEditDiagnostic,
    isUploadedDiagnosticProcessing,
    onChangeDiagnosticPress,
    onSearchDiagnosticPress,
    cvtDiagnosticsJobId,
    sbrResponse,
    vin,
    uploadedDiagnostic,
}: DiagnosticSummaryProps) {
    const classes = useStyles();
    const { t } = useTranslation();

    const hasAttachedDiagnostic = Boolean(cvtDiagnosticsJobId);

    if (hasAttachedDiagnostic) {
        return (
            <DiagnosticSummaryCard
                buttonLabel={t('qmr:sections.failCode.cvtDiagnostic.changeDiagnostic', 'Change Test')}
                buttonVariant="ghost-blue"
                iconName="paperclip"
                isButtonDisabled={isUploadedDiagnosticProcessing}
                onButtonPress={canEditDiagnostic ? onChangeDiagnosticPress : undefined}
            >
                <div className={classes.headingRow}>
                    <DiagnosticLink diagnosticId={cvtDiagnosticsJobId} vin={vin}>
                        {cvtDiagnosticsJobId}
                    </DiagnosticLink>

                    <DiagnosticResultBadge
                        diagnosticId={cvtDiagnosticsJobId}
                        sbrResponseData={sbrResponse?.sbrResponseData}
                    />
                </div>
                <Typography color="textDarkSecondary" variant="caption">
                    {formatDateWithTimeAgo(sbrResponse?.jobCompletedTimestamp, DATE_FORMAT.MMM_D_YEAR)}
                </Typography>
                {isUploadedDiagnosticProcessing && (
                    <div className="d-flex align-items-center gap-2">
                        <LoadingCircle borderWidth={2} size={14} />
                        <Typography color="textDarkSecondary" variant="caption">
                            {t(
                                'qmr:sections.failCode.cvtDiagnostic.uploadedDiagnostic.replacing',
                                'Replacing in progress ({{diagnosticId}})',
                                { diagnosticId: uploadedDiagnostic?.diagnosticId }
                            )}
                        </Typography>
                    </div>
                )}
            </DiagnosticSummaryCard>
        );
    }

    if (isUploadedDiagnosticProcessing) {
        return (
            <DiagnosticSummaryCard
                buttonLabel={t('qmr:sections.failCode.cvtDiagnostic.searchDiagnostic', 'Search Test')}
                buttonVariant="primary"
                isButtonDisabled
                isLoading
                onButtonPress={onSearchDiagnosticPress}
            >
                <SkeletonLoader className="mb-1" height={14} type="text" width="40%" />
                <SkeletonLoader className="mb-0" height={10} type="text" width="30%" />
            </DiagnosticSummaryCard>
        );
    }

    return (
        <DiagnosticSummaryCard
            buttonLabel={t('qmr:sections.failCode.cvtDiagnostic.searchDiagnostic', 'Search Test')}
            buttonVariant="primary"
            description={t(
                'qmr:sections.failCode.cvtDiagnostic.requiresRecentDiagnostic',
                'A CVT-related fail code requires a diagnostic test for this VIN from the last 30 days.'
            )}
            iconName="paperclip"
            onButtonPress={canEditDiagnostic ? onSearchDiagnosticPress : undefined}
            title={t('qmr:sections.failCode.cvtDiagnostic.noDiagnosticAttached', 'No CVT diagnostic test attached')}
        />
    );
}
